import {
    ConfirmationDialog,
    ContactForm,
    ContactListItem,
    LoadingSkeleton,
    MandatorListItem,
    Modal,
    PageGridItem,
    Typography,
} from 'ComponentLibrary'
import { FunnelFooter } from 'ClientPortal/src/pages/Orders/CreateOrder/FunnelFooter/FunnelFooter'
import { type StepProps } from 'ClientPortal/src/pages/Orders/CreateOrder/types'
import React, { useEffect, useState } from 'react'
import { Stack, styled, useTheme } from '@mui/material'
import type { Contact as ContactSchema } from '@einwert/valuation-api-client'
import { MandatorSelect } from 'ComponentLibrary/src/molecules/MandatorList/MandatorSelect'
import { Mandator } from 'ComponentLibrary/src/molecules/MandatorList/types'
import { isEmpty } from 'lodash'
import { PlusCircleIcon } from '@phosphor-icons/react'
import { useTranslation } from 'react-i18next'
import { Headline } from 'ComponentLibrary/src/piazza/text/Headline/Headline'
import { useUserLinkCopy } from 'ClientPortal/src/i18n/useUserLinkCopy'

type ClientStepProps = {
    prefilledMandators: Mandator[]
    isMandatorsPending?: boolean
    onCancel: () => void
} & StepProps

export function MandatorStep({
    prefilledMandators,
    isMandatorsPending,
    activeStep,
    handleNext,
    isLastStep = false,
    dispatch,
    order,
    onCancel,
}: ClientStepProps) {
    const { t } = useTranslation('pages', { keyPrefix: 'orders.createOrderPage.mandator' })
    const { t: tCommonActions } = useTranslation('terminology', { keyPrefix: 'common.actions' })
    const theme = useTheme()
    const mandatorFormLabels = {
        name: t('mandatorForm.name'),
        street: t('mandatorForm.street'),
        houseNumber: t('mandatorForm.houseNumber'),
        zip: t('mandatorForm.zip'),
        city: t('mandatorForm.city'),
        country: t('mandatorForm.country'),
    }
    const mandatorSelectCopy = {
        inputLabel: t('mandatorSelect.inputLabel'),
        addNewLabel: t('mandatorSelect.addNew'),
        addModalTitle: t('mandatorSelect.addModalTitle'),
        requiredField: t('mandatorSelect.requiredField'),
        formLabels: mandatorFormLabels,
    }
    const mandatorListItemCopy = {
        edit: t('mandatorListItem.edit'),
        delete: t('mandatorListItem.delete'),
        editModalTitle: t('mandatorListItem.editModalTitle'),
        formLabels: mandatorFormLabels,
    }
    const contactFormCopy = {
        salutation: t('contactForm.salutation'),
        salutationOptions: {
            mr: t('contactForm.salutationOptions.mr'),
            ms: t('contactForm.salutationOptions.ms'),
            dr: t('contactForm.salutationOptions.dr'),
            mrDr: t('contactForm.salutationOptions.mrDr'),
            msDr: t('contactForm.salutationOptions.msDr'),
            placeholder: t('contactForm.salutationOptions.placeholder'),
        },
        name: t('contactForm.name'),
        role: t('contactForm.role'),
        company: t('contactForm.company'),
        street: t('contactForm.street'),
        houseNumber: t('contactForm.houseNumber'),
        zip: t('contactForm.zip'),
        city: t('contactForm.city'),
        email: t('contactForm.email'),
        invalidEmail: t('contactForm.invalidEmail'),
        phoneNumber: t('contactForm.phoneNumber'),
        invalidPhoneNumber: t('contactForm.invalidPhoneNumber'),
        note: t('contactForm.note'),
        infoBox: t('contactForm.infoBox'),
    }
    const contactListItemCopy = {
        edit: t('contactListItem.edit'),
        delete: t('contactListItem.delete'),
        editModalTitle: t('contactListItem.editModalTitle'),
        form: contactFormCopy,
    }
    const userLinkCopy = useUserLinkCopy()

    const [isAddContactFormOpen, setIsAddContactFormOpen] = useState(false)
    const [isAddMandatorSelectOpen, setIsAddMandatorSelectOpen] = useState(false)
    const [isEditingPrimaryMandator, setIsEditingPrimaryMandator] = useState(false)
    const [isError, setIsError] = useState(false)
    const [showConfirmationDialog, setShowConfirmationDialog] = useState(false)

    // Hide error if the add new mandator form has been opened
    useEffect(() => setIsError(false), [isEditingPrimaryMandator])

    const handleNextStep = () => {
        if (order.mandators.length === 0) {
            setIsError(true)
        } else if (isAddContactFormOpen || isAddMandatorSelectOpen || isEditingPrimaryMandator) {
            setShowConfirmationDialog(true)
        } else {
            handleNext()
        }
    }

    const updateMandator = (data: Mandator, index: number) => {
        setIsError(false)
        dispatch({ type: 'updateMandator', payload: data, index })
    }

    const createOrUpdatePrimaryMandator = (data: Mandator) => {
        setIsError(false)
        if (order.mandators.length === 0) {
            dispatch({ type: 'addMandator', payload: data })
        } else {
            dispatch({ type: 'updateMandator', payload: data, index: 0 })
        }
    }

    if (isMandatorsPending) {
        return (
            <PageGridItem columnSpan={3} columnSpanXs={4}>
                <LoadingSkeleton count={3} height="180px" width="100%" />
            </PageGridItem>
        )
    }

    return (
        <PageGridItem columnSpan={3} columnSpanXs={4}>
            <Stack direction="column" gap={theme.spacing(8)}>
                <Stack direction="column" gap={theme.spacing(6)}>
                    <MandatorSelect
                        defaultValue={order.mandators[0] ?? ''}
                        error={isError}
                        options={prefilledMandators}
                        selectMandator={createOrUpdatePrimaryMandator}
                        setIsCreatingNewMandator={setIsEditingPrimaryMandator}
                        copy={mandatorSelectCopy}
                    />
                    {order.mandators[0] && !isEditingPrimaryMandator && (
                        <MandatorListItem
                            mandator={order.mandators[0]}
                            updateMandator={(data) => updateMandator(data, 0)}
                            copy={mandatorListItemCopy}
                        />
                    )}
                </Stack>
                <Stack direction="column">
                    <Headline
                        title={t('additionalMandatorsTitle')}
                        variant="headline2"
                        withSpacing
                        iconButtons={[
                            {
                                icon: <PlusCircleIcon />,
                                onClick: () => setIsAddMandatorSelectOpen(true),
                                disabled: isEmpty(order.mandators),
                                tooltip: isEmpty(order.mandators) ? t('additionalMandatorsDisabledTooltip') : undefined,
                                ariaLabel: t('addAdditionalMandatorAriaLabel'),
                            },
                        ]}
                    />
                    <Stack direction="column" gap={theme.spacing(8)}>
                        <Typography variant="bodyTextLarge" color={theme.palette.customtext.black100}>
                            {t('additionalMandatorsQuestion')}
                        </Typography>
                        {isAddMandatorSelectOpen && (
                            <MandatorSelect
                                defaultValue={''}
                                options={prefilledMandators}
                                setIsMandatorSelectOpen={setIsAddMandatorSelectOpen}
                                selectMandator={(data) => dispatch({ type: 'addMandator', payload: data })}
                                copy={mandatorSelectCopy}
                            />
                        )}
                    </Stack>
                    <StyledListWrapper style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(4) }}>
                        {order.mandators.map(
                            (mandator, index) =>
                                index !== 0 && (
                                    <MandatorListItem
                                        key={index}
                                        mandator={mandator}
                                        updateMandator={(data) => updateMandator(data, index)}
                                        deleteMandator={() => dispatch({ type: 'removeMandator', index })}
                                        copy={mandatorListItemCopy}
                                    />
                                )
                        )}
                    </StyledListWrapper>
                </Stack>

                <Stack direction="column">
                    <Headline
                        title={t('additionalContactsTitle')}
                        variant="headline2"
                        withSpacing
                        iconButtons={[
                            {
                                icon: <PlusCircleIcon />,
                                onClick: () => setIsAddContactFormOpen(true),
                                tooltip: t('addContactTitle'),
                                ariaLabel: t('addContactAriaLabel'),
                            },
                        ]}
                    />
                    <Typography variant="bodyTextLarge" color={theme.palette.customtext.black100}>
                        {t('additionalContactsDescription')}
                    </Typography>
                    {isAddContactFormOpen && (
                        <Modal
                            isOpen={isAddContactFormOpen}
                            showCloseIcon
                            closeIconTooltip={tCommonActions('close')}
                            onClose={() => setIsAddContactFormOpen(false)}
                            title={t('addContactTitle')}
                        >
                            <ContactForm
                                setFormIsOpen={setIsAddContactFormOpen}
                                onSubmitCallback={(data) => dispatch({ type: 'addContact', payload: data })}
                                copy={contactFormCopy}
                            />
                        </Modal>
                    )}
                    <StyledListWrapper>
                        {order.contacts.map((contact, index) => {
                            return (
                                <ContactListItem
                                    key={index}
                                    contact={contact}
                                    updateContact={(payload: ContactSchema) => dispatch({ type: 'updateContact', payload, index })}
                                    deleteContact={() => dispatch({ type: 'removeContact', index })}
                                    copy={contactListItemCopy}
                                    userLinkCopy={userLinkCopy}
                                />
                            )
                        })}
                    </StyledListWrapper>
                </Stack>
                <FunnelFooter activeStep={activeStep} isLastStep={isLastStep} handleNext={handleNextStep} onCancel={onCancel} />
                <ConfirmationDialog
                    title={t('discardUnsavedChanges')}
                    open={showConfirmationDialog}
                    onCancel={() => setShowConfirmationDialog(false)}
                    onConfirm={handleNext}
                    cancelLabel={tCommonActions('cancel')}
                />
            </Stack>
        </PageGridItem>
    )
}

const StyledListWrapper = styled('ul')`
    list-style: none;
    padding: 0;
`
